import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import SeoHead from '../components/SeoHead';
import {
  subscribeNotifications,
  markAllNotificationsRead,
  migrateLocalStorageNotifications,
  notificationThreadLink,
  FirestoreNotification,
} from '../repositories/notificationsRepository';
import { openThreadFromNotification } from '../lib/threadDeepLink';
import { Paths } from '../repositories/firestoreUtils';
import { getDoc } from 'firebase/firestore';

/* ---- Chat Summary Modal Types ---- */
type ChatMessage = { role: string; content: string; timestamp?: number | { seconds: number } };
type ChatSummaryData = {
  session_id: string;
  original_query?: string;
  conversation_history: ChatMessage[];
  message_count?: number;
  created_at?: any;
  updated_at?: any;
  customer_email?: string;
  visitor_name?: string;
};

const PageContainer = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  padding: 2.5rem 1.25rem 3.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: clamp(1.6rem, 2.4vw, 2rem);
  margin: 0 0 0.25rem;
  color: var(--brand-text);
  text-transform: lowercase;
`;

const Subtitle = styled.p`
  margin: 0 0 0.75rem;
  color: #555;
  font-size: 1rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 10px 28px rgba(0,0,0,0.08);
  overflow: hidden;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ $unread?: boolean }>`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  align-items: flex-start;
  transition: background 0.2s ease;
  background: ${props => props.$unread ? 'rgba(74, 144, 226, 0.04)' : 'transparent'};

  &:hover {
    background: rgba(0,0,0,0.02);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Icon = styled.div`
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  color: #4A90E2;
  margin-top: 2px;
`;

const RowBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const RowTitle = styled.div`
  font-weight: 700;
  color: var(--brand-black);
  font-size: 1.02rem;
`;

const RowMessage = styled.div`
  color: #444;
  font-size: 0.95rem;
  line-height: 1.45;
  word-break: break-word;
`;

const RowMeta = styled.div`
  color: var(--brand-text-muted);
  font-size: 0.82rem;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  border-top: 1px solid rgba(0,0,0,0.06);
`;

const FooterInfo = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const FooterButton = styled.button`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0,0,0,0.08);
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f6ff;
    border-color: rgba(33, 150, 243, 0.25);
    box-shadow: 0 2px 10px rgba(33, 150, 243, 0.12);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  color: #666;
`;

const ReportCard = styled.div`
  margin-top: 6px;
  padding: 10px 12px;
  background: #f8f9ff;
  border-radius: 10px;
  border: 1px solid rgba(74, 144, 226, 0.15);
`;

const ReportMeta = styled.div`
  font-size: 0.85rem;
  color: #555;
  margin-bottom: 6px;
`;

const ReportButton = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid rgba(74, 144, 226, 0.3);
  background: white;
  color: #4A90E2;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #4A90E2;
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ---- Chat Summary Modal Styles ---- */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 720px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  color: var(--brand-text);
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  font-size: 1.4rem;
  cursor: pointer;
  color: var(--brand-text-muted);
  padding: 4px 8px;
  border-radius: 6px;
  &:hover { background: #f0f0f0; }
`;

const ModalBody = styled.div`
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
`;

const SummaryMeta = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 14px;
  line-height: 1.6;
`;

const ChatBubble = styled.div<{ $isUser?: boolean }>`
  margin-bottom: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 0.92rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 90%;
  ${props => props.$isUser ? `
    background: #e8f0fe;
    color: var(--brand-black);
    margin-left: auto;
    border-bottom-right-radius: 4px;
  ` : `
    background: #f5f5f5;
    color: #333;
    margin-right: auto;
    border-bottom-left-radius: 4px;
  `}
`;

const BubbleLabel = styled.div<{ $isUser?: boolean }>`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: lowercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  color: ${props => props.$isUser ? '#4A90E2' : '#888'};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BubbleTime = styled.span`
  font-size: 0.65rem;
  font-weight: 400;
  text-transform: none;
  color: var(--brand-text-muted);
  letter-spacing: 0;
`;

const Notifications: React.FC = () => {
  const toAppCase = (input: string) => {
    if (!input) return '';
    const lowered = input.toLowerCase();
    return lowered.replace(/\bolv\b/g, 'OLV').replace(/\bai\b/g, 'AI');
  };

  const { isAuthenticated, user } = useAuth();
  const [items, setItems] = useState<FirestoreNotification[]>([]);
  const [page, setPage] = useState<number>(1);
  const [downloadingReport, setDownloadingReport] = useState<string | null>(null);
  const [chatSummary, setChatSummary] = useState<ChatSummaryData | null>(null);
  const [loadingSummary, setLoadingSummary] = useState<string | null>(null);
  const pageSize = 10;

  // One-time migration from localStorage to Firestore
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !user?.accountId) return;
    migrateLocalStorageNotifications(user.id, user.accountId).catch(() => {});
  }, [isAuthenticated, user?.id, user?.accountId]);

  // Subscribe to real-time Firestore notifications
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setItems([]);
      return;
    }
    const unsubscribe = subscribeNotifications(
      user.id,
      (notifications) => setItems(notifications),
      (err) => console.warn('Failed to load notifications:', err),
    );
    return unsubscribe;
  }, [isAuthenticated, user?.id]);

  // Mark all notifications as read when visiting the page
  useEffect(() => {
    if (!isAuthenticated || !user?.id || items.length === 0) return;
    const hasUnread = items.some((n) => !n.read);
    if (hasUnread) {
      markAllNotificationsRead(user.id).catch(() => {});
    }
  }, [isAuthenticated, user?.id, items]);

  const handleViewReport = useCallback(async (reportId: string) => {
    if (!user?.id) return;
    setDownloadingReport(reportId);
    try {
      const ref = Paths.widgetReportDoc(user.id, reportId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        alert('Report not found.');
        return;
      }
      const data = snap.data();
      if (data.pdf_base64) {
        const byteChars = atob(data.pdf_base64);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteArray[i] = byteChars.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else if (data.report_content) {
        // Fallback: open report content in a new window
        const win = window.open('', '_blank');
        if (win) {
          win.document.write(`<pre style="white-space:pre-wrap;font-family:sans-serif;padding:2rem;">${data.report_content}</pre>`);
          win.document.title = data.report_title || 'Report';
        }
      }
    } catch (err) {
      console.error('Failed to load report:', err);
    } finally {
      setDownloadingReport(null);
    }
  }, [user?.id]);

  /** Open a chat summary modal for a session-ended or chat-session notification */
  const handleViewChatSummary = useCallback(async (sessionId: string) => {
    if (!user?.id) return;
    setLoadingSummary(sessionId);
    try {
      // Chat sessions are stored as widget_reports/chat_{sessionId}
      const chatDocId = sessionId.startsWith('chat_') ? sessionId : `chat_${sessionId}`;
      const ref = Paths.widgetReportDoc(user.id, chatDocId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        alert('Chat summary not found for this session.');
        return;
      }
      const data = snap.data() as ChatSummaryData;
      setChatSummary({
        session_id: data.session_id || sessionId,
        original_query: data.original_query,
        conversation_history: data.conversation_history || [],
        message_count: data.message_count,
        created_at: data.created_at,
        updated_at: data.updated_at,
        customer_email: data.customer_email,
        visitor_name: data.visitor_name,
      });
    } catch (err) {
      console.error('Failed to load chat summary:', err);
      alert('Failed to load chat summary.');
    } finally {
      setLoadingSummary(null);
    }
  }, [user?.id]);

  /** Sanitize assistant content: strip code blocks, summarize widget/tool references */
  const sanitizeContent = (content: string): string => {
    if (!content) return '';
    // Remove code blocks (```...```)
    let cleaned = content.replace(/```[\s\S]*?```/g, '[code block removed]');
    // Remove inline code
    cleaned = cleaned.replace(/`[^`]+`/g, '[code]');
    // Summarize HTML/widget references
    cleaned = cleaned.replace(/<[^>]+>/g, '');
    // Collapse multiple newlines
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    return cleaned.trim();
  };

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const slice = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page]);

  const formatDateTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch {
      return iso;
    }
  };

  return (
    <>
      <SeoHead
        tabTitle="notifications"
        title="Notifications | prest0"
        description="review your latest updates, alerts, and activity across prest0"
      />
      <PageContainer>
      <Title>notifications</Title>
      <Subtitle>your latest alerts and updates. stay informed.</Subtitle>
      <Card>
        <List>
          {slice.length === 0 ? (
            <Empty>no notifications yet</Empty>
          ) : (
            slice.map((n) => (
              <Row key={n.id} $unread={!n.read}>
                <Icon>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 01-3.46 0"/>
                  </svg>
                </Icon>
                <RowBody>
                  <RowTitle>{toAppCase(n.title || 'notification')}</RowTitle>
                  <RowMessage>{toAppCase(n.message)}</RowMessage>
                  <RowMeta>{formatDateTime(n.timestamp)}</RowMeta>
                  {/* Deep Research Report button */}
                  {n.metadata?.report_id && n.metadata?.type !== 'session_ended' && n.metadata?.type !== 'chat_session' && (
                    <ReportCard>
                      {n.metadata.customer_email && (
                        <ReportMeta>client: {n.metadata.customer_email}</ReportMeta>
                      )}
                      {n.metadata.report_title && (
                        <ReportMeta>report: {n.metadata.report_title}</ReportMeta>
                      )}
                      <ReportButton
                        onClick={() => handleViewReport(n.metadata!.report_id)}
                        disabled={downloadingReport === n.metadata.report_id}
                      >
                        {downloadingReport === n.metadata.report_id ? 'loading...' : 'view report'}
                      </ReportButton>
                    </ReportCard>
                  )}
                  {/* Thread-scoped rows open their thread (2026-09-08): same
                      workspace → the chat opens on it; another workspace →
                      the sealed switch, never an in-place foreign open. */}
                  {notificationThreadLink(n) && (
                    <ReportCard data-notification-thread-id={notificationThreadLink(n)!.threadId}>
                      <ReportButton
                        type="button"
                        data-notification-open-thread=""
                        onClick={() => openThreadFromNotification(notificationThreadLink(n)!)}
                      >
                        open thread
                      </ReportButton>
                    </ReportCard>
                  )}
                  {/* Chat Summary button for session-ended and chat-session notifications */}
                  {n.metadata?.session_id && (n.metadata?.type === 'session_ended' || n.metadata?.type === 'chat_session') && (
                    <ReportCard>
                      {n.metadata.visitor_name && (
                        <ReportMeta>visitor: {n.metadata.visitor_name}</ReportMeta>
                      )}
                      {n.metadata.customer_email && (
                        <ReportMeta>client: {n.metadata.customer_email}</ReportMeta>
                      )}
                      {n.metadata.message_count && (
                        <ReportMeta>messages: {n.metadata.message_count}</ReportMeta>
                      )}
                      <ReportButton
                        onClick={() => handleViewChatSummary(n.metadata!.session_id)}
                        disabled={loadingSummary === n.metadata.session_id}
                      >
                        {loadingSummary === n.metadata.session_id ? 'loading...' : 'view chat summary'}
                      </ReportButton>
                    </ReportCard>
                  )}
                </RowBody>
              </Row>
            ))
          )}
        </List>
        <Footer>
          <FooterInfo>
            page {Math.min(page, totalPages)} of {totalPages} ({items.length} total)
          </FooterInfo>
          <FooterButtons>
            <FooterButton disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>previous</FooterButton>
            <FooterButton disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>next</FooterButton>
          </FooterButtons>
        </Footer>
      </Card>
    </PageContainer>

    {/* Chat Summary Modal */}
    {chatSummary && (
      <ModalOverlay onClick={() => setChatSummary(null)}>
        <ModalBox onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>chat summary</ModalTitle>
            <ModalClose onClick={() => setChatSummary(null)}>&times;</ModalClose>
          </ModalHeader>
          <ModalBody>
            <SummaryMeta>
              {chatSummary.original_query && (
                <div><strong>topic:</strong> {chatSummary.original_query}</div>
              )}
              {chatSummary.customer_email && (
                <div><strong>client:</strong> {chatSummary.customer_email}</div>
              )}
              {chatSummary.visitor_name && (
                <div><strong>visitor:</strong> {chatSummary.visitor_name}</div>
              )}
              <div><strong>messages:</strong> {chatSummary.message_count || chatSummary.conversation_history.length}</div>
              {chatSummary.updated_at?.toDate && (
                <div><strong>last activity:</strong> {chatSummary.updated_at.toDate().toLocaleString()}</div>
              )}
            </SummaryMeta>

            {chatSummary.conversation_history.length === 0 ? (
              <Empty>no messages found for this session</Empty>
            ) : (
              chatSummary.conversation_history.map((msg, idx) => (
                <div key={idx}>
                  <BubbleLabel $isUser={msg.role === 'user'}>
                    {msg.role === 'user' ? 'client' : 'assistant'}
                    {msg.timestamp && (
                      <BubbleTime>
                        {new Date(typeof msg.timestamp === 'number' ? msg.timestamp : msg.timestamp?.seconds ? msg.timestamp.seconds * 1000 : Date.now()).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </BubbleTime>
                    )}
                  </BubbleLabel>
                  <ChatBubble $isUser={msg.role === 'user'}>
                    {sanitizeContent(msg.content)}
                  </ChatBubble>
                </div>
              ))
            )}
          </ModalBody>
        </ModalBox>
      </ModalOverlay>
    )}
    </>
  );
};

export default Notifications;
